import { useEffect, useState } from 'react';

import { getRoutesDirections } from '@/data/libraries/Routes';
import { Layouts } from '@/data/libraries/Layouts';

import { useLocation, useNavigate, type NavigateFunction, Location } from 'react-router-dom';

import { useScreenSize, ScreenSize } from '@/ui/hooks/useScreenSize';

import { Container } from 'react-bootstrap';
import './style.css';

import { useAppSelector, useAppDispatch } from '@/ui/store/helperRedux';
import { updateShippingTypes } from '@/ui/store/slices/shippingTypesSlice';
import { RootState } from '@/ui/store/store';

import { changeBlockedByCheckoutState, changeLoginState, changeRegisterState } from '@/ui/store/slices/showModalsSlice';

import { getUrlFromCssVariable } from '@/domain/utils/CssToImage';
import MenuController from '@/controllers/MenuController';
import ShippingTypesController from '@/controllers/ShippingTypesController';

import { IShippingTypes } from '@/domain/interfaces/IProduct';
import { IStore } from '@/domain/interfaces/IStore';

import LoginForm from '@/ui/components/user/LoginForm';
import SignUpForm from '@/ui/components/user/SignUpForm';
import PasswordRecovery from '@/ui/components/user/PasswordRecovery';
import ResetPassword from '@/ui/components/user/ResetPassword';
import PasswordRecoveryCode from '@/ui/components/user/PasswordRecoveryCode';
import CartModal from '@/ui/components/modals/CartModal';

import ModalNavUser from '@/ui/containers/userOptions/ModalNavUser';
import HeaderDesktop from '@/ui/containers/Header/HeaderDesktop';
import HeaderMobile from '@/ui/containers/Header/HeaderMobile';


/**
 * Componente Header.
 * 
 * Este componente representa el encabezado de la aplicación y contiene la lógica para manejar
 * la navegación, el estado de la tienda, el estado del usuario, y la visualización de varios modales.
 * 
 * @returns {JSX.Element} El componente Header.
 * 
 * @remarks
 * - Utiliza varios hooks de React y Redux para manejar el estado y la navegación.
 * - Contiene funciones para mostrar y ocultar modales de login, registro, carrito, y recuperación de contraseña.
 * - Realiza consultas a controladores para obtener datos de la tienda y tipos de envío.
 * 
 * @component
 * 
 * @example
 * <Header />
 * 
 * @hook
 * - useAppDispatch: Hook para despachar acciones de Redux.
 * - useAppSelector: Hook para seleccionar el estado de Redux.
 * - useNavigate: Hook para la navegación dentro de la aplicación.
 * - useLocation: Hook para obtener la ubicación actual de la aplicación.
 * - useScreenSize: Hook para obtener el tamaño de la pantalla.
 * - useState: Hook para manejar el estado local del componente.
 * - useEffect: Hook para manejar efectos secundarios en el componente.
 * 
 * @function handleShowLogin
 * Muestra el modal de login o las opciones de usuario dependiendo del estado de autenticación del usuario.
 * 
 * @function handleShowRegister
 * Muestra el modal de registro.
 * 
 * @function handleShowOptionsUser
 * Muestra u oculta las opciones de usuario.
 * 
 * @function handleShowCartModal
 * Muestra u oculta el modal del carrito.
 * 
 * @function fetchShippingTypes
 * Consulta los tipos de envío disponibles en la tienda y actualiza el estado de Redux.
 * 
 * @function configureStoreData
 * Configura los datos de la tienda y actualiza el estado de Redux.
 * 
 * @function getMenu
 * Obtiene el menú principal de la tienda.
 * 
 * @function handleResetPassword
 * Muestra u oculta el modal de recuperación de contraseña.
 * 
 * @function handleExpand
 * Expande o contrae el menú.
 * 
 * @function goToPurchases
 * Redirige a la página de compras o de seguimiento dependiendo del estado de autenticación del usuario.
 */

interface HeaderProps {
  setShowLoginAlert?: (value: boolean) => void;
  setShowRegisterAlert?: (value: boolean) => void;
}

const Header = ({
  setShowLoginAlert,
  setShowRegisterAlert
}: HeaderProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { getShippingTypes } = ShippingTypesController();
  const { mainMenu } = MenuController();

  const LOGO = getUrlFromCssVariable('--img-logo-header');
  const LOGO_DEFAULT_LOGO = getUrlFromCssVariable('--img-logo-header-default');
  const LOGO_HEADER_MOBILE_DEFAULT = getUrlFromCssVariable('--img-logo-header-mobile-default');

  /** Hook para obtener la ubicación actual de la aplicación. */
  const location: Location = useLocation();
  /** Hook para generar una navegación dentro de la aplicación. */
  const navigate: NavigateFunction = useNavigate();
  /** Hook para obtener el estado del usuario logueado. */
  const userLogued = useAppSelector((state) => state.userState);
  /** Hook para obtener el tamaño de la pantalla. */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  /** Variable para determinar si la pantalla es móvil. */
  const isMobile: boolean = width < maxLargeWidth;
  /** Estado para mostrar opciones de usuario. */
  const [showOptionsUser, setShowOptionsUser] = useState(false);
  /** Estado para mostrar el modal del carrito. */
  const [showCartModal, setShowCartModal] = useState<boolean>(false);
  /** Estado para mostrar el modal de login. */
  const showLogin: boolean = useAppSelector((state) => state.showModals.showLogin);
  /** Estado para mostrar el modal de registro. */
  const showRegister: boolean = useAppSelector((state) => state.showModals.showRegister);
  /** Estado para mostrar el modal de recuperación de contraseña. */
  const showPasswordRecovery: boolean = useAppSelector((state) => state.showModals.showRecovery);
  /** Estado para mostrar el modal de recuperación de contraseña con código. */
  const showPasswordRecoveryCode: boolean = useAppSelector((state) => state.showModals.showRecoveryCode);
  /** Estado para mostrar el modal de recuperación de contraseña con código. */
  const [email, setEmail] = useState<string>('');
  /** Estado para mostrar el modal de recuperación de contraseña con código. */
  const [showResetPasswordModal, setShowResetPasswordModal] = useState<boolean>(false);
  /** Estado para mostrar el modal de recuperación de contraseña con código. */
  const [authCode, setAuthCode] = useState<string>('');
  /** Estado para mostrar el menú expandido. */
  const [isExpanded, setIsExpanded] = useState<boolean>(false);


  const language = useAppSelector((state) => state.language.language);

  /** Función para mostrar el modal de login o las opciones de usuario. */
  const handleShowLogin = (): void => {
    if (userLogued.user.isLogued) {
      //mostrar opciones de usuario
      handleShowOptionsUser();
    } else {
      dispatch(changeLoginState());
    }

  };

  /** Función para mostrar el modal de registro. */
  const handleShowRegister = (): void => {
    dispatch(changeRegisterState());
  };

  /** Función para mostrar las opciones de usuario. */
  const handleShowOptionsUser = () => setShowOptionsUser(!showOptionsUser);

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /**Estado Redux para mostrar modal checkout */
  const showBlockedByCheckout: boolean = useAppSelector((state: RootState) => state.showModals.showBlockedByCheckout);
  /** Modifica el State Redux para modal checkout */
  const handleStatusmodalExitConfirmation = (): void => { dispatch(changeBlockedByCheckoutState()) };


  /** Función para mostrar el modal del carrito. */
  const handleShowCartModal = (): void => {
    if (location.pathname === routesDirections.CART_ROUTE) return;
    if (location.pathname === routesDirections.CHECK_OUT_ROUTE) {
      !showBlockedByCheckout && handleStatusmodalExitConfirmation();
      navigate(routesDirections.CART_ROUTE);
    } else {
      setShowCartModal(!showCartModal);
    }
  };

  /** Consulta tipos de envío disponibles en la tienda  */
  const fetchShippingTypes = async (): Promise<void> => {
    /**Trae la información del tipos de entrega desde Redux */
    const shippingTypesResponse: IShippingTypes[] = await getShippingTypes();
    if (shippingTypesResponse) {
      /** Ordena los tipos de entrega */
      shippingTypesResponse.sort((a, b) => a.id - b.id);
      dispatch(updateShippingTypes([...shippingTypesResponse]));
    }
  };

  /** Obtiene el menú principal de la tienda */
  const getMenu = async (): Promise<void> => { try { await mainMenu(); } catch (err) { console.error(err); } };

  /** UseEffect para renderizado de Menu */
  useEffect(() => {
    getMenu();
  }, [language]);

  /** Guarda Tipos de entrega disponibles */
  useEffect(() => {
    fetchShippingTypes();
  }, []);

  /** Función para mostrar el modal de recuperación de contraseña. */
  const handleResetPassword = (): void => setShowResetPasswordModal(!showResetPasswordModal);
  /** Función para expandir el menú. */
  const handleExpand = (): void => setIsExpanded(!isExpanded);

  /** Función para redirigir a la página de compras o de seguimiento. */
  const goToPurchases = () => {
    if (userLogued.user.isLogued) {
      navigate(routesDirections.PURCHASES_ROUTE);
    } else {
      navigate(routesDirections.TRACKING_ROUTE);
    }
  }



  /** Estado Redux Tienda */
  const storeData = useAppSelector((state: RootState) => state.storeData) as IStore | null;
  /** Renderizado del componente */
  return (
    <Container fluid id='header' className={`g-0 bg-primary-3 sticky-top p-0 m-0 shadow ${!isMobile ? 'px-5 pt-1 ' : 'pt-1 pb-2'}`}>
      {!isMobile ? (

        <HeaderDesktop
          logo={`${storeData?.layoutCode === Layouts.DEFAULT ? LOGO_DEFAULT_LOGO : LOGO}`}
          isExpanded={isExpanded}
          handleExpand={handleExpand}
          handleShowCartModal={handleShowCartModal}
          handleShowLogin={handleShowLogin}
          handleShowRegister={handleShowRegister}
          handleShowOptionsUser={handleShowOptionsUser}
          goToPurchases={goToPurchases}
        />
      ) : (
        <HeaderMobile
          logo={LOGO_HEADER_MOBILE_DEFAULT}
          handleShowOptionsUser={handleShowOptionsUser}
        />
      )}

      {showOptionsUser && (
        <ModalNavUser
          showOptionsUser={showOptionsUser}
          handleShowOptionsUser={handleShowOptionsUser}
          fullscreen={width <= maxLargeWidth ? true : ''}
        />
      )}

      {showCartModal && <CartModal handleClose={handleShowCartModal} />}
      {showLogin && <LoginForm setShowLoginAlert={setShowLoginAlert} />}
      {showRegister && <SignUpForm setShowRegisterAlert={setShowRegisterAlert} />}
      {showPasswordRecovery && <PasswordRecovery setEmail={setEmail} />}
      {showPasswordRecoveryCode && (
        <PasswordRecoveryCode email={email} setAuthCode={setAuthCode} handleResetPassword={handleResetPassword} />
      )}
      {showResetPasswordModal && (
        <ResetPassword
          email={email}
          auhCode={authCode}
          showResetPassword={showResetPasswordModal}
          handleResetPassword={handleResetPassword}
        />
      )}
    </Container>
  );
};

export default Header;